import { useEffect, useMemo, useState } from 'react';
import { Banknote, Upload, FileDown } from 'lucide-react';
import { Button, Card, Pill, useToast } from '@nako/ui';
import { formatMoney } from '@nako/core';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';

interface Account {
  id: string;
  gl_code: string;
  name: string;
  type: string;
}

interface JournalLine {
  id: string;
  entry_id: string;
  account_id: string;
  debit: number;
  credit: number;
  description: string | null;
}

interface JournalEntry {
  id: string;
  business_id: string;
  entry_date: string;
  reference: string | null;
  description: string | null;
  source: string;
}

interface CashRow {
  id: string;
  entry_id: string;
  date: string;
  description: string;
  source: string;
  debit: number;
  credit: number;
  amount: number; // signed
  balance: number;
  reconciled: boolean;
}

const RECON_KEY = (bizId: string) => `np.cashbook.reconciled.${bizId}`;

export default function Cashbook() {
  const business = useAuthStore((s) => s.business)!;
  const regionCode = useRegionStore((s) => s.code);
  const toast = useToast();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [lines, setLines] = useState<JournalLine[]>([]);
  const [cash, setCash] = useState<Account | null>(null);
  const [reconciled, setReconciled] = useState<Set<string>>(new Set());
  const [statementBalance, setStatementBalance] = useState<number | null>(null);
  const [statementImport, setStatementImport] = useState<
    Array<{ date: string; amount: number; ref: string }>
  >([]);
  const [tab, setTab] = useState<'cashbook' | 'reconcile'>('cashbook');

  useEffect(() => {
    void load();
  }, [business.id]);

  async function load() {
    // Entries first, then lines filtered by entry_id — defence in depth:
    // RLS scopes this server-side, but the explicit filter prevents a single
    // misconfigured policy from leaking another tenant's ledger.
    const [{ data: a }, { data: e }] = await Promise.all([
      supabase
        .from('accounts')
        .select('*')
        .eq('business_id', business.id)
        .eq('gl_code', '1000')
        .maybeSingle(),
      supabase
        .from('journal_entries')
        .select('*')
        .eq('business_id', business.id)
        .order('entry_date'),
    ]);
    const entryIds = (e ?? []).map((x) => x.id);
    const { data: l } =
      entryIds.length === 0
        ? { data: [] }
        : await supabase.from('journal_lines').select('*').in('entry_id', entryIds);
    setCash(a as Account | null);
    setEntries((e ?? []) as JournalEntry[]);
    setLines((l ?? []) as JournalLine[]);

    try {
      const raw = localStorage.getItem(RECON_KEY(business.id));
      if (raw) setReconciled(new Set(JSON.parse(raw)));
    } catch {
      // ignore
    }
  }

  function persistReconciled(set: Set<string>) {
    setReconciled(new Set(set));
    try {
      localStorage.setItem(RECON_KEY(business.id), JSON.stringify(Array.from(set)));
    } catch {
      // ignore
    }
  }

  function toggle(lineId: string) {
    const next = new Set(reconciled);
    if (next.has(lineId)) next.delete(lineId);
    else next.add(lineId);
    persistReconciled(next);
  }

  const rows: CashRow[] = useMemo(() => {
    if (!cash) return [];
    const cashLines = lines.filter((l) => l.account_id === cash.id);
    const out: CashRow[] = [];
    let running = 0;
    // Sort entries by date asc so the running balance is correct
    const byEntry = new Map(entries.map((e) => [e.id, e]));
    const sorted = cashLines
      .map((l) => ({ line: l, entry: byEntry.get(l.entry_id) }))
      .filter((x) => x.entry)
      .sort((a, b) => a.entry!.entry_date.localeCompare(b.entry!.entry_date));

    for (const { line, entry } of sorted) {
      const amount = line.debit - line.credit;
      running = round2(running + amount);
      out.push({
        id: line.id,
        entry_id: line.entry_id,
        date: entry!.entry_date,
        description: line.description ?? entry!.description ?? entry!.reference ?? 'Movement',
        source: entry!.source,
        debit: line.debit,
        credit: line.credit,
        amount,
        balance: running,
        reconciled: reconciled.has(line.id),
      });
    }
    return out.reverse(); // newest first for display
  }, [cash, lines, entries, reconciled]);

  const ledgerBalance = rows[0]?.balance ?? 0;
  const reconciledTotal = useMemo(
    () => rows.filter((r) => r.reconciled).reduce((s, r) => s + r.amount, 0),
    [rows],
  );
  const unreconciledTotal = useMemo(
    () => rows.filter((r) => !r.reconciled).reduce((s, r) => s + r.amount, 0),
    [rows],
  );
  const reconDifference =
    statementBalance !== null ? round2(statementBalance - reconciledTotal) : null;

  function handleStatementUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const parsed = parseStatementCsv(text);
      setStatementImport(parsed);
      const total = parsed.reduce((s, x) => s + x.amount, 0);
      setStatementBalance(round2(total));
      toast.show(
        `Loaded ${parsed.length} statement lines, balance ${formatMoney(round2(total), regionCode)}.`,
        {
          tone: 'success',
        },
      );
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  function autoMatch() {
    // Heuristic: match each statement line to the closest unreconciled
    // ledger line within 3 days that has the same amount.
    const next = new Set(reconciled);
    let matched = 0;
    for (const sl of statementImport) {
      const candidate = rows
        .filter((r) => !next.has(r.id))
        .find(
          (r) =>
            Math.abs(r.amount - sl.amount) < 0.01 && Math.abs(daysBetween(r.date, sl.date)) <= 3,
        );
      if (candidate) {
        next.add(candidate.id);
        matched++;
      }
    }
    persistReconciled(next);
    toast.show(`Auto-matched ${matched} of ${statementImport.length} lines.`, { tone: 'success' });
  }

  function downloadCsv() {
    const csv = [
      'Date,Description,Source,Debit,Credit,Balance,Reconciled',
      ...rows.map((r) =>
        [r.date, q(r.description), r.source, r.debit, r.credit, r.balance, r.reconciled].join(','),
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cashbook_${business.name.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display flex items-center gap-2 text-2xl">
          <Banknote size={22} /> Cashbook
        </h1>
      </div>

      {/* Summary */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3">
        <Card compact>
          <p className="text-muted text-xs">Ledger balance</p>
          <p className="tnum font-mono text-lg">{formatMoney(ledgerBalance, regionCode)}</p>
        </Card>
        <Card compact>
          <p className="text-muted text-xs">Reconciled</p>
          <p className="tnum text-green font-mono text-lg">
            {formatMoney(reconciledTotal, regionCode)}
          </p>
        </Card>
        <Card compact>
          <p className="text-muted text-xs">Unreconciled</p>
          <p className="tnum text-accent font-mono text-lg">
            {formatMoney(unreconciledTotal, regionCode)}
          </p>
        </Card>
      </div>

      <div className="mb-4 flex gap-2">
        {(['cashbook', 'reconcile'] as const).map((tt) => (
          <button
            key={tt}
            onClick={() => setTab(tt)}
            className={`rounded-pill border px-3 py-1.5 text-xs font-medium ${
              tab === tt ? 'bg-ink text-paper border-ink' : 'border-line text-muted bg-white'
            }`}
          >
            {tt === 'cashbook' ? 'Cashbook' : 'Bank reconciliation'}
          </button>
        ))}
      </div>

      {tab === 'reconcile' && (
        <Card className="mb-4">
          <div className="space-y-3">
            <div>
              <p className="text-muted text-xs">Bank statement balance</p>
              <p className="tnum font-mono text-2xl">
                {statementBalance !== null ? formatMoney(statementBalance, regionCode) : '—'}
              </p>
            </div>
            {reconDifference !== null && (
              <div
                className={`rounded-card p-3 text-sm ${
                  Math.abs(reconDifference) < 0.01 ? 'bg-green/10 text-green' : 'bg-red/10 text-red'
                }`}
              >
                {Math.abs(reconDifference) < 0.01
                  ? `✓ Reconciled — bank statement matches reconciled ledger lines exactly.`
                  : `Difference: ${formatMoney(reconDifference, regionCode)} — ${
                      reconDifference > 0
                        ? 'statement is higher (you may have missed posting some receipts).'
                        : 'ledger is higher (some posted entries are not yet on the statement).'
                    }`}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <label className="bg-paper-2 hover:bg-line rounded-btn min-h-tap flex cursor-pointer items-center justify-center gap-2 text-sm font-medium">
                <Upload size={14} />
                Upload statement CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleStatementUpload}
                />
              </label>
              <Button
                variant="secondary"
                onClick={autoMatch}
                disabled={statementImport.length === 0}
              >
                Auto-match
              </Button>
            </div>
            <p className="text-muted text-[11px]">
              CSV format: <span className="font-mono">date,description,amount</span> · ISO dates ·
              positive amounts for deposits, negative for withdrawals.
            </p>
          </div>
        </Card>
      )}

      <div className="mb-3 flex justify-end">
        <button
          onClick={downloadCsv}
          className="text-muted hover:text-ink inline-flex items-center gap-1 text-xs"
        >
          <FileDown size={12} /> Export CSV
        </button>
      </div>

      {/* Cashbook rows */}
      {rows.length === 0 ? (
        <Card className="py-10 text-center">
          <Banknote size={32} className="text-muted mx-auto mb-3 opacity-50" />
          <p className="text-muted">No cash movements yet.</p>
        </Card>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => (
            <Card
              key={r.id}
              compact
              className={`flex items-center gap-2 ${
                r.reconciled ? 'bg-green/5 border-green/20' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={r.reconciled}
                onChange={() => toggle(r.id)}
                className="h-4 w-4"
                aria-label="Mark reconciled"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{r.description}</p>
                <p className="text-muted text-xs">
                  {r.date} ·{' '}
                  <Pill tone="neutral" className="text-[10px]">
                    {r.source}
                  </Pill>
                </p>
              </div>
              <div className="text-right">
                <p className={`tnum font-mono text-sm ${r.amount < 0 ? 'text-red' : 'text-green'}`}>
                  {r.amount < 0 ? '−' : '+'} {formatMoney(Math.abs(r.amount), regionCode)}
                </p>
                <p className="text-muted tnum font-mono text-[10px]">
                  bal {formatMoney(r.balance, regionCode)}
                </p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </main>
  );
}

// ============================================================
// Helpers
// ============================================================

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function daysBetween(a: string, b: string): number {
  return Math.floor((new Date(a).getTime() - new Date(b).getTime()) / 86_400_000);
}

function q(s: string): string {
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

interface StatementLine {
  date: string;
  amount: number;
  ref: string;
}

function parseStatementCsv(text: string): StatementLine[] {
  const out: StatementLine[] = [];
  const lines = text.split(/\r?\n/).filter(Boolean);
  // Skip header if first row is non-numeric in last column
  const start =
    /[a-z]/i.test(lines[0] ?? '') && !/^\d/.test((lines[0] ?? '').split(',')[2] ?? '') ? 1 : 0;
  for (let i = start; i < lines.length; i++) {
    const cols = parseCsvRow(lines[i] ?? '');
    if (cols.length < 3) continue;
    const date = (cols[0] ?? '').trim();
    const ref = (cols[1] ?? '').trim();
    const amt = Number((cols[2] ?? '').replace(/[\s,]/g, ''));
    if (!date || isNaN(amt)) continue;
    out.push({ date, amount: amt, ref });
  }
  return out;
}

function parseCsvRow(row: string): string[] {
  const cols: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < row.length; i++) {
    const c = row[i];
    if (c === '"') {
      if (inQ && row[i + 1] === '"') {
        cur += '"';
        i++;
        continue;
      }
      inQ = !inQ;
      continue;
    }
    if (c === ',' && !inQ) {
      cols.push(cur);
      cur = '';
      continue;
    }
    cur += c;
  }
  cols.push(cur);
  return cols;
}
